import { useMemo } from "react";

import { Observation } from "@medplum/fhirtypes";

import { List, ListItem, Table } from "@mantine/core";

import range from "lodash/range";
import { Fragment } from "react/jsx-runtime";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getFHRRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface FHRRowProps {
  time: string;
  observation: Observation | undefined;
  showNumbersOnly?: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
  isPreviousVersion?: boolean;
}

export default function FHRRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
  isPreviousVersion,
}: FHRRowProps) {
  // Extract FHR and related values from observation
  const {
    fhr,
    measured,
    listeningDevice,
    accelerationsDetected,
    decelerationsDetected,
    baseline,
    baselineFHR,
    baselineDuration,
    relationToContractions,
    durationOfListening,
    notes,
    hasAnswers,
  } = useMemo(() => getFHRRowValues(observation), [observation]);

  if (!observation) return null;

  const previousVersions = hasPreviousVersions(observation);

  // If no FHR and no other answers, render nothing
  if (!fhr && !hasAnswers) return null;

  // Determine if details row is needed
  const detailsRowNeeded = !showNumbersOnly && (hasAnswers || notes || fhr);

  // Row span for TimeCell and ActionCell
  const rowSpan = fhr && detailsRowNeeded && !showNumbersOnly ? 2 : 1;

  const showTitle =
    measured ||
    listeningDevice ||
    accelerationsDetected !== undefined ||
    decelerationsDetected !== undefined ||
    baseline !== undefined ||
    baselineFHR ||
    baselineDuration ||
    relationToContractions ||
    durationOfListening;

  return (
    <Fragment>
      {/* FHR value row (main row) */}
      {fhr && showPreviousVersions && (
        <Table.Tr bg="white">
          <TimeCell
            time={time}
            rowSpan={rowSpan}
            isViewOnly={isViewOnly}
            observation={observation}
          />
          <Table.Td>{fhr}</Table.Td>
          {range(9).map((i) => (
            <Table.Td key={i} />
          ))}
          {!isViewOnly && <ActionCell rowSpan={rowSpan} observation={observation} />}
        </Table.Tr>
      )}
      {/* Details row (with previous versions, notes, etc.) */}
      {detailsRowNeeded && (
        <Table.Tr bg="white" className={classes.tableRow}>
          {showPreviousVersions && !fhr && (
            <TimeCell
              time={time}
              rowSpan={rowSpan}
              isViewOnly={isViewOnly}
              observation={observation}
            />
          )}
          <Table.Td p="md" colSpan={10}>
            {showTitle && (
              <TextWrapper pt="xs" size="sm">
                FHR:
              </TextWrapper>
            )}
            <List fz="sm" withPadding>
              {isPreviousVersion && fhr && (
                <ListItem>
                  <strong>FHR:</strong> {fhr}
                </ListItem>
              )}
              {measured && measured !== `${durationOfListening} seconds` && (
                <ListItem>
                  <strong>Measured:</strong> {measured}
                </ListItem>
              )}
              {durationOfListening && (
                <ListItem>
                  <strong>Duration of Listening:</strong> {durationOfListening} seconds
                </ListItem>
              )}
              {listeningDevice && (
                <ListItem>
                  <strong>Listening Device:</strong> {listeningDevice}
                </ListItem>
              )}
              {relationToContractions && (
                <ListItem>
                  <strong>Relation to Contractions:</strong> {relationToContractions}
                </ListItem>
              )}
              {accelerationsDetected !== undefined && (
                <ListItem>
                  <strong>
                    {accelerationsDetected ? "Accelerations detected" : "No accelerations detected"}
                  </strong>
                </ListItem>
              )}
              {decelerationsDetected !== undefined && (
                <ListItem>
                  <strong>
                    {decelerationsDetected ? "Decelerations detected" : "No decelerations detected"}
                  </strong>
                </ListItem>
              )}
              {baseline !== undefined && (
                <ListItem>
                  <strong>{baseline ? "Baseline:" : "Baseline unchanged"}</strong>
                  {baseline && (
                    <List fz="sm" withPadding>
                      {baselineFHR && (
                        <ListItem>
                          <strong>FHR:</strong> {baselineFHR}
                        </ListItem>
                      )}
                      {baselineDuration && (
                        <ListItem>
                          <strong>Duration:</strong> {baselineDuration}
                        </ListItem>
                      )}
                    </List>
                  )}
                </ListItem>
              )}
            </List>
            {notes && !showNumbersOnly && (
              <TextWrapper pt="xs" size="sm">
                FHR Comments: {notes}
              </TextWrapper>
            )}
            {showPreviousVersions && previousVersions ? (
              <PreviousVersions
                rowComponent={FHRRow}
                observation={observation}
                showPreviousVersions={showPreviousVersions}
              />
            ) : (
              <LastUpdated observation={observation} showPreviousVersions={false} />
            )}
          </Table.Td>
          {(!fhr || !showPreviousVersions) && !isPreviousVersion && !isViewOnly && (
            <ActionCell rowSpan={1} observation={observation} />
          )}
        </Table.Tr>
      )}
    </Fragment>
  );
}
